import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type AiBillingInput, type MaskedKey, type TenantBase } from '../lib/api';
import { formatEur } from '../lib/format';
import { AppliedHint } from './AppliedHint';
import { Button, Card, ErrorText, inputClass, Modal } from './ui';

type Source = 'firmio' | 'own' | null;

const SOURCE_LABELS: Record<'firmio' | 'own', { title: string; hint: string }> = {
  firmio: {
    title: 'Abonament Firmio (cheile Clean Code Culture)',
    hint: 'Instanța folosește cheile Anthropic / OpenAI introduse aici pentru client. Tu setezi limita lunară și modelele.',
  },
  own: {
    title: 'Cheia proprie a clientului',
    hint: 'Clientul își pune cheile în Setări → Integrări și își gestionează singur bugetul și modelele.',
  },
};

/** One provider's key: required for the subscription, write-only. */
function KeyField({
  label,
  current,
  value,
  onChange,
}: {
  label: string;
  current: MaskedKey;
  value: string;
  onChange: (v: string) => void;
}) {
  const missing = !current.set && value.trim() === '';
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-400">
          {label} <span className="text-red-300">*</span>
        </span>
        <span className={`text-xs ${missing ? 'text-amber-300' : 'text-slate-500'}`}>
          {current.set ? `setată · …${current.last4}` : 'lipsă (obligatorie)'}
        </span>
      </div>
      <input
        type="password"
        autoComplete="off"
        placeholder={
          current.set
            ? 'Lasă gol ca să păstrezi cheia; lipește alta ca s-o înlocuiești'
            : 'Lipește cheia'
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} mt-1`}
      />
    </div>
  );
}

/**
 * Who pays for the tenant's AI. With the Firmio subscription the instance
 * runs on the Anthropic and OpenAI keys entered here for this customer (both
 * required) under the limit set here; with its own keys the customer manages
 * everything in the app. Keys are write-only: only their last 4 characters
 * ever come back.
 */
export function AiBillingCard({
  tenant,
  lastSeenAt,
  aiTotalEur,
}: {
  tenant: TenantBase;
  lastSeenAt: string | null;
  aiTotalEur: number | null;
}) {
  const queryClient = useQueryClient();
  const saved = tenant.aiBilling;
  const [source, setSource] = useState<Source>(saved.source);
  const [budget, setBudget] = useState(saved.budgetEur == null ? '' : String(saved.budgetEur));
  const [anthropicKey, setAnthropicKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [confirming, setConfirming] = useState(false);

  const budgetNumber = budget.trim() === '' ? null : Number(budget.replace(',', '.'));
  const budgetInvalid =
    budgetNumber !== null && (!Number.isFinite(budgetNumber) || budgetNumber < 0);
  const keyTooShort = [anthropicKey, openaiKey].some(
    (k) => k.trim() !== '' && k.trim().length < 10,
  );
  const missingKeys =
    source === 'firmio'
      ? [
          !saved.anthropic.set && !anthropicKey.trim() ? 'Anthropic' : null,
          !saved.openai.set && !openaiKey.trim() ? 'OpenAI' : null,
        ].filter((k): k is string => k !== null)
      : [];

  const input: AiBillingInput = {
    source,
    ...(source === 'firmio'
      ? {
          budgetEur: budgetNumber,
          ...(anthropicKey.trim() ? { anthropicKey: anthropicKey.trim() } : {}),
          ...(openaiKey.trim() ? { openaiKey: openaiKey.trim() } : {}),
        }
      : {}),
  };

  const dirty =
    source !== saved.source ||
    (source === 'firmio' &&
      (budgetNumber !== saved.budgetEur || Boolean(anthropicKey.trim() || openaiKey.trim())));

  const mutation = useMutation({
    mutationFn: () => api.setTenantAiBilling(tenant.id, input),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries();
    },
  });

  function reset() {
    setSource(saved.source);
    setBudget(saved.budgetEur == null ? '' : String(saved.budgetEur));
    setAnthropicKey('');
    setOpenaiKey('');
  }

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-white">Cheie API și facturare AI</h2>
        <p className="mt-1 text-xs text-slate-400">
          Cine plătește AI-ul clientului: abonamentul Firmio sau clientul, cu cheile lui.
        </p>
      </div>

      <div className="space-y-2">
        {(['firmio', 'own'] as const).map((s) => (
          <label
            key={s}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 ${
              source === s ? 'border-violet-500/60 bg-violet-500/10' : 'border-slate-800'
            }`}
          >
            <input
              type="radio"
              name="ai-source"
              checked={source === s}
              onChange={() => setSource(s)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm text-white">{SOURCE_LABELS[s].title}</span>
              <span className="block text-xs text-slate-400">{SOURCE_LABELS[s].hint}</span>
            </span>
          </label>
        ))}
        {saved.source === null && source === null && (
          <p className="text-xs text-slate-500">
            Nesetat: instanța folosește ce cheie are configurată (Setări sau server), ca până acum.
          </p>
        )}
      </div>

      {source === 'firmio' && (
        <div className="space-y-4 rounded-lg border border-slate-800 p-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-400">
              Limită lunară AI (EUR)
            </span>
            <input
              inputMode="decimal"
              placeholder="fără limită"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className={`${inputClass} mt-1`}
            />
            <span className="mt-1 block text-xs text-slate-500">
              La 80% adminii clientului primesc o alertă; la limită AI-ul se oprește până luna
              viitoare.
              {aiTotalEur != null && <> Consumat luna aceasta: {formatEur(aiTotalEur)}.</>}
            </span>
            {budgetInvalid && <span className="text-xs text-red-300">Număr pozitiv sau gol.</span>}
          </label>
          <KeyField
            label="Cheie Anthropic"
            current={saved.anthropic}
            value={anthropicKey}
            onChange={setAnthropicKey}
          />
          <KeyField
            label="Cheie OpenAI"
            current={saved.openai}
            value={openaiKey}
            onChange={setOpenaiKey}
          />
          {keyTooShort && (
            <p className="text-xs text-red-300">O cheie are cel puțin 10 caractere.</p>
          )}
          {missingKeys.length > 0 && (
            <p className="text-xs text-amber-300">
              Pentru abonamentul Firmio sunt obligatorii cheile {missingKeys.join(' și ')}.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <AppliedHint savedAt={tenant.updatedAt} lastSeenAt={lastSeenAt} />
        <div className="flex gap-2">
          <Button variant="ghost" disabled={!dirty} onClick={reset}>
            Renunță
          </Button>
          <Button
            disabled={!dirty || budgetInvalid || keyTooShort || missingKeys.length > 0}
            onClick={() => setConfirming(true)}
          >
            Salvează
          </Button>
        </div>
      </div>

      {confirming && (
        <Modal
          title={`Salvezi facturarea AI pentru ${tenant.name}?`}
          onClose={() => setConfirming(false)}
        >
          <div className="space-y-3 text-sm text-slate-300">
            {source === 'firmio' && (
              <>
                <p>
                  Instanța va folosi cheile introduse aici pentru client. Cheile din Setări →
                  Integrări ale clientului se ignoră, iar bugetul din aplicație devine doar pentru
                  citire.
                </p>
                <p>
                  Limită lunară: {budgetNumber == null ? 'fără limită' : formatEur(budgetNumber)}.
                </p>
              </>
            )}
            {source === 'own' && (
              <p>
                Instanța va folosi doar cheile clientului din Setări → Integrări. Cheile de aici nu
                se mai trimit, iar modelele nu mai sunt plafonate din consolă.
              </p>
            )}
            <ErrorText error={mutation.error} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Renunță
              </Button>
              <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                Salvează
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}
